import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, FileText, Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const LOCK_IMG = "https://images.unsplash.com/photo-1633265486064-086b219458ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjcmVkaXQlMjBjYXJkJTIwc2VjdXJpdHklMjBsb2NrfGVufDB8fHx8MTc4OTE0MzYzNnww&ixlib=rb-4.1.0&q=85";

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [params] = useSearchParams();
  const [docType, setDocType] = useState(params.get("type") === "ftc_report" ? "ftc_report" : "id_proof");
  const [bureau, setBureau] = useState("experian");
  const [busy, setBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/portal/overview").then((r) => setDocs(r.data.documents)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose a file first");
    const fd = new FormData();
    fd.append("file", f);
    fd.append("doc_type", docType);
    fd.append("bureau", (docType === "credit_report" || docType === "bureau_response") ? bureau : "");
    setBusy(true);
    try {
      const r = await api.post("/portal/documents", fd);
      if (docType === "bureau_response") {
        const res = r.data.ai_result;
        if (res) {
          setAiResult(res);
          toast.success(res.matched > 0 ? `We read the letter — ${res.matched} item${res.matched === 1 ? "" : "s"} updated on your file` : "Letter saved — we couldn't match it to an open dispute yet; your case team will review");
        } else toast.success("Letter uploaded — your case team will review it");
      } else toast.success("Document uploaded securely");
      fileRef.current.value = "";
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const open = async (id) => {
    try {
      const r = await api.get(`/portal/documents/${id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="portal-documents">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">My documents</h1>
        <p className="text-slate-600 mt-1">Upload your ID, proof of address, and credit report PDFs from Equifax, Experian, and TransUnion. We'll handle the rest.</p>
        <p className="text-sm mt-2"><Link to="/portal/guides#free-reports" className="text-blue-700 font-medium hover:underline" data-testid="docs-guide-free-reports-link">How do I get my reports for free?</Link> · <Link to="/portal/guides#fraud" className="text-rose-700 font-medium hover:underline" data-testid="docs-guide-fraud-link">I see accounts I don't recognize</Link></p>
      </div>

      <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white p-6 space-y-4" data-testid="report-upload-dropzone">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>What are you uploading?</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="mt-1" data-testid="portal-doc-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="id_proof">Government-issued ID</SelectItem>
                <SelectItem value="address_proof">Proof of address (utility bill, lease)</SelectItem>
                <SelectItem value="credit_report">Credit report (PDF)</SelectItem>
                <SelectItem value="ssn_card">Social Security card</SelectItem>
                <SelectItem value="ftc_report">FTC Identity Theft Report</SelectItem>
                <SelectItem value="bureau_response">Bureau response letter (reply to a dispute)</SelectItem>
                <SelectItem value="other">Something else</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(docType === "credit_report" || docType === "bureau_response") && (
            <div>
              <Label>{docType === "bureau_response" ? "Which bureau sent it?" : "Which bureau?"}</Label>
              <Select value={bureau} onValueChange={setBureau}>
                <SelectTrigger className="mt-1" data-testid="portal-bureau-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="experian">Experian</SelectItem>
                  <SelectItem value="transunion">TransUnion</SelectItem>
                  <SelectItem value="equifax">Equifax</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Input type="file" ref={fileRef} className="max-w-xs" accept={docType === "credit_report" ? "application/pdf" : docType === "bureau_response" ? "application/pdf,image/*" : undefined} data-testid="portal-doc-file-input" />
          <Button onClick={upload} disabled={busy} data-testid="client-document-upload-btn" className="bg-[#0B2545] hover:bg-[#134074] text-white">
            <UploadCloud className="h-4 w-4 mr-2" /> {busy ? (docType === "bureau_response" ? "Reading the letter…" : "Uploading…") : "Upload securely"}
          </Button>
        </div>
        {docType === "bureau_response" && <p className="text-xs text-slate-500">Got a letter back from the bureau? Upload the PDF or a clear photo — our AI reads it and updates each disputed item (deleted, updated, or verified) automatically.</p>}
        {aiResult && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm space-y-2" data-testid="bureau-response-result">
            <div className="font-semibold text-emerald-900">What the bureau said</div>
            <p className="text-emerald-800">{aiResult.summary}</p>
            {aiResult.results?.length > 0 && (
              <ul className="space-y-1">
                {aiResult.results.map((r) => (
                  <li key={r.item_id} className="flex items-start gap-2 text-xs text-emerald-900" data-testid={`response-item-${r.item_id.slice(0, 8)}`}>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 font-semibold uppercase ${r.outcome === "deleted" ? "bg-emerald-600 text-white" : r.outcome === "verified" ? "bg-amber-500 text-white" : r.outcome === "updated" ? "bg-blue-600 text-white" : "bg-slate-300 text-slate-700"}`}>{r.outcome}</span>
                    <span>{r.note}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="px-5 py-3 border-b border-slate-100 text-xs font-mono uppercase tracking-[0.18em] text-slate-500">Your vault ({docs.length})</div>
        <div className="divide-y divide-slate-100">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between px-5 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{d.filename}</div>
                  <div className="text-xs text-slate-400">{d.type.replace("_", " ")} {d.bureau ? `· ${d.bureau}` : ""} · {fmtDate(d.created_at)}</div>
                </div>
              </div>
              <button onClick={() => open(d.id)} className="text-slate-500 hover:text-slate-800 transition-colors duration-150" data-testid={`portal-doc-download-${d.id.slice(0, 8)}`}>
                <Download className="h-4 w-4" />
              </button>
            </div>
          ))}
          {docs.length === 0 && <div className="px-5 py-10 text-center text-sm text-slate-400">Nothing uploaded yet. Your documents will appear here.</div>}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 flex items-center gap-4">
        <img src={LOCK_IMG} alt="Encrypted storage" className="h-14 w-14 rounded-lg object-cover hidden sm:block" />
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          <p className="text-sm text-slate-600 leading-relaxed">Every file is encrypted and stored in your private vault. Only your case team can see it, and every access is recorded.</p>
        </div>
      </div>
    </div>
  );
}
